import axios from 'axios';

/** Resolve API base URL. In local dev, call the backend directly to avoid CRA webpack proxy 504/timeouts. */
function resolveApiBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL?.trim();
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === 'development') {
    return 'http://127.0.0.1:5345/api/v1';
  }
  return '/api/v1';
}

export const API_BASE_URL = resolveApiBaseUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests (skip only for public feedback / supplier-quote routes - they use token in query)
// generate-link and /feedback/list require auth - do NOT skip those
api.interceptors.request.use((config) => {
  const url = typeof config.url === 'string' ? config.url : '';
  const isPublicFeedback = url.includes('/feedback/order/') && !url.includes('/generate-link');
  // Public supplier quick-quote link: /quote/order/:id  and  /quote/order/:id/submit
  const isPublicSupplierQuote = url.includes('/quote/order/');
  // Public customer approve-and-pay page: /public/job-cards/:id
  const isPublicJobCard = url.includes('/public/job-cards/');
  // The delivery note's QR page: /public/delivery-notes/:token
  const isPublicDeliveryNote = url.includes('/public/delivery-notes/');
  if (isPublicFeedback || isPublicSupplierQuote || isPublicJobCard || isPublicDeliveryNote) {
    // Public surface: never send Authorization (suppliers/customers have no login)
    delete config.headers.Authorization;
  } else {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (
      window.location.pathname !== '/login' &&
      !window.location.pathname.startsWith('/feedback/') &&
      !window.location.pathname.startsWith('/quote/')
    ) {
      console.warn('No token found in localStorage');
    }
  }
  // For FormData, remove Content-Type so browser sets multipart/form-data with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Safely check for error response
    if (error && error.response) {
      // Don't logout for: payment errors, login page, feedback page, or feedback API requests
      const reqUrl = error.config?.url || '';
      const isFeedbackRequest = typeof reqUrl === 'string' && reqUrl.includes('feedback/order');
      const isFeedbackPage = window.location.pathname.startsWith('/feedback/');
      const isSupplierQuoteRequest = typeof reqUrl === 'string' && reqUrl.includes('/quote/order/');
      const isSupplierQuotePage = window.location.pathname.startsWith('/quote/');
      const isJobCardPublicRequest = typeof reqUrl === 'string' && reqUrl.includes('/public/job-cards/');
      const isJobCardPublicPage = window.location.pathname.startsWith('/jc/');
      const isDeliveryNotePage = window.location.pathname.startsWith('/d/');
      const shouldLogout =
        error.response.status === 401 &&
        error.response.data?.error?.code !== 'PAYMENT_GATEWAY_ERROR' &&
        window.location.pathname !== '/login' &&
        !isFeedbackPage &&
        !isFeedbackRequest &&
        !isSupplierQuotePage &&
        !isSupplierQuoteRequest &&
        !isJobCardPublicPage &&
        !isJobCardPublicRequest &&
        !isDeliveryNotePage;

      if (shouldLogout) {
        const token = localStorage.getItem('token');
        if (token) {
          console.warn('401 Unauthorized - logging out user');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

